import './index.css'
// Self-hosted fonts (replaces the render-blocking Google Fonts <link>).
// Type system: Poppins for headings (500/600/700), Open Sans for
// everything else (400/500/600/700). Only the latin + latin-ext subsets -
// these typefaces don't cover cyrillic/greek/vietnamese, and translated
// non-latin scripts fall back to system fonts, so shipping those subsets'
// @font-face rules would just bloat the render-blocking CSS.
// font-display:swap is built in.
import '@fontsource/open-sans/latin-400.css'
import '@fontsource/open-sans/latin-500.css'
import '@fontsource/open-sans/latin-600.css'
import '@fontsource/open-sans/latin-700.css'
import '@fontsource/open-sans/latin-ext-400.css'
import '@fontsource/open-sans/latin-ext-500.css'
import '@fontsource/open-sans/latin-ext-600.css'
import '@fontsource/open-sans/latin-ext-700.css'
import '@fontsource/poppins/latin-500.css'
import '@fontsource/poppins/latin-600.css'
import '@fontsource/poppins/latin-700.css'
import '@fontsource/poppins/latin-ext-500.css'
import '@fontsource/poppins/latin-ext-600.css'
import '@fontsource/poppins/latin-ext-700.css'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom'
import HtmlPage from './HtmlPage.jsx'
import NotFound from './NotFound.jsx'
import { isKnownRoute } from './RouteSEO.jsx'
import FooterLanguageSwitcher from './FooterLanguageSwitcher.jsx'
import RevealFixer from './RevealFixer.jsx'
import ImageTuner from './ImageTuner.jsx'
import StyleOverrides from './StyleOverrides.jsx'
import LogoThemeSwap from './LogoThemeSwap.jsx'
// TopBarLanguageSwitcher removed per request - the top bar no longer shows a
// language chip. FooterLanguageSwitcher still mounts the dropdown in the
// footer, so translation itself is untouched.
import DetailsAutoClose from './DetailsAutoClose.jsx'
import NavActive from './NavActive.jsx'
import FooterPromoter from './FooterPromoter.jsx'
import RouteSEO from './RouteSEO.jsx'
import Breadcrumbs from './Breadcrumbs.jsx'
import BrandProtector from './BrandProtector.jsx'
import CookieNotice from './CookieNotice.jsx'
import FaqAccordion from './FaqAccordion.jsx'
import FaqSchema from './FaqSchema.jsx'
import ClientCarousel from './ClientCarousel.jsx'
import ServicesCarousel from './ServicesCarousel.jsx'
// TestimonialCarousel removed: #reviews is now a static grid of quote cards
// (.tq-*), so no [data-testi-carousel] element exists for it to drive.
import ProductsCarousel from './ProductsCarousel.jsx'
import FrameworksCarousel from './FrameworksCarousel.jsx'
import HeroIntro from './HeroIntro.jsx'
import TopicHeroIntro from './TopicHeroIntro.jsx'
import HeroVideoPause from './HeroVideoPause.jsx'
import FrameworksWave from './FrameworksWave.jsx'
import ScrollReveals from './ScrollReveals.jsx'
import ProductSlideTransition from './ProductSlideTransition.jsx'
import CtaReveal from './CtaReveal.jsx'
import FlowDraw from './FlowDraw.jsx'

import mainHtml from '../Pentabay.html?raw'

/* Every path renders the same HtmlPage shell. Inside that shell, the
   inline router in Pentabay.html reads window.location.pathname and
   shows the matching .page element from window.PB_PAGES. Real S3
   folders (/ava/, /mydart/, /neo/, /weguard/, /zentra/, /assets/,
   /uploads/, /screenshots/, /design-review/, /videos/) never reach
   React because CloudFront serves their actual index.html files
   directly.

   THAT LIST IS A CONSTRAINT, NOT JUST A NOTE: a folder in public/ wins
   over the identically-named React route, and it wins ONLY on a real
   HTTP request. In-app navigation is client-side, so the route works
   when you click it and breaks when you open it in a new tab, which
   makes it look like a rendering bug rather than a routing one.
   2026-08-08: public/design/ was doing exactly that to /design (the UI
   UX Design Services page) - it held an internal design-review artifact
   with no inbound links, and S3 redirects /design to /design/ once an
   index document exists there, so the marketing route was unreachable
   by direct URL. Renamed to public/design-review/. Before adding any
   folder to public/, check its name against the routes registered in
   pentabay-subpages.js / pentabay-pages.js.

   AN UNRECOGNISED PATH RENDERS <NotFound> INSTEAD, added 2026-09-30. Until
   this, *every* path rendered HtmlPage unconditionally - the vanilla router
   inside Pentabay.html has its own fallback for a route it doesn't
   recognise, and that fallback is to silently show the HOME page's content
   (see redirectRetiredRoutes()'s neighbouring comment in Pentabay.html for
   the same failure mode described from the other side). A search engine
   landing on a dead/typo'd URL saw a normal-looking 200 page, identical to
   the homepage - a soft 404, flagged directly by a DM team technical SEO
   audit. isKnownRoute() (src/RouteSEO.jsx) is the single source of truth
   for "does this app have a real page here", shared with RouteSEO's own
   apply() so the visible page and the title/canonical/meta tags always
   agree - showing NotFound's UI while RouteSEO still asserted the homepage's
   canonical would have been no improvement at all.
   This only fixes what real browsers and JS-rendering crawlers see - the
   HTTP status code for these paths is still 200 today. That needs a
   CloudFront distribution change this app can't make unilaterally (see
   infra/README.md); this is the safe half of the fix that doesn't touch
   AWS. */
function App() {
  const { pathname } = useLocation()
  const known = isKnownRoute(pathname)
  return (
    <>
      <Routes>
        <Route
          path="*"
          element={known ? <HtmlPage html={mainHtml} pageKey="main" /> : <NotFound />}
        />
      </Routes>
      <RevealFixer />
      <HeroIntro />
      <TopicHeroIntro />
      <HeroVideoPause />
      <FrameworksWave />
      <ScrollReveals />
      <ProductSlideTransition />
      <CtaReveal />
      <FlowDraw />
      <ImageTuner />
      <StyleOverrides />
      <LogoThemeSwap />
      <DetailsAutoClose />
      <NavActive />
      <FooterPromoter />
      <RouteSEO />
      <Breadcrumbs />
      <FaqSchema />
      <FaqAccordion />
      <ClientCarousel />
      <ServicesCarousel />
      <ProductsCarousel />
      <FrameworksCarousel />
      <BrandProtector />
      <CookieNotice />
      <FooterLanguageSwitcher />
    </>
  )
}

// Opt in to the two React Router v7 behaviour changes so we don't ship
// with deprecation warnings to the console. Safe in v6.x; flips the
// router to v7's already-shipping behaviour (transition-wrapped state
// updates + relative-splat path resolution).
createRoot(document.getElementById('root')).render(
  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <App />
  </BrowserRouter>
)
